import { useState, useEffect } from "react";
import axios from "axios";
import { Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "./ui/button";
import { ScrollArea } from "./ui/scroll-area";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "./ui/collapsible";
import {
  Map,
  Building2,
  Grid3X3,
  BookOpen,
  GitBranch,
  Shield,
  Users,
  FileText,
  Calculator,
  Wallet,
  BarChart3,
  Settings,
  UserCircle,
  ChevronDown,
  ChevronRight,
  Menu,
  X,
  LogOut,
  Bell,
  Search,
  Home,
  CreditCard,
  Percent,
  DollarSign,
  Briefcase,
  LayoutDashboard,
  Car,
  ShoppingBag,
  Truck,
  Receipt,
  ClipboardList,
  ShieldAlert,
  Wine
} from "lucide-react";

// Submódulos de Definiciones
const definicionesSubmenus = [
  { id: "bancos", title: "Bancos", icon: Building2 },
  { id: "cuentas", title: "Cuentas Bancarias", icon: CreditCard },
  { id: "impuestos", title: "Tipos de Impuestos", icon: Percent },
  { id: "presupuesto", title: "Códigos Presupuestarios", icon: FileText },
  { id: "oficinas", title: "Oficinas", icon: Building2 },
  { id: "tipos-calculo", title: "Tipos de Cálculo", icon: Calculator },
  { id: "cotizaciones", title: "Cotización Moneda", icon: DollarSign },
  { id: "actividades", title: "Actividad Económica", icon: Briefcase },
  { id: "configuracion-general", title: "Configuración", icon: Settings },
  { id: "manuales", title: "Manuales", icon: BookOpen },
  { id: "flujogramas", title: "Flujogramas", icon: GitBranch }
];

// Submódulos de Contribuyentes
const contribuyentesSubmenus = [
  { id: "con-lae", title: "Contribuyente con LAE", icon: FileText },
  { id: "sin-lae", title: "Contribuyente sin LAE", icon: Users }
];

// Submódulos de Catastro
const catastroSubmenus = [
  { id: "ficha-catastral", title: "Ficha Catastral", icon: FileText },
  { id: "tabla-pvt", title: "Tabla Planta de Valores de la Tierra", icon: Calculator },
  { id: "tabla-tvc", title: "Tabla de Valores de la Construcción", icon: Calculator },
  { id: "tabla-depreciacion", title: "Tabla Depreciación", icon: Percent },
  { id: "clasificador-inmuebles", title: "Clasificador Inmuebles", icon: Building2 },
  { id: "boletin-calculo", title: "Boletín de Cálculo", icon: FileText },
  { id: "solvencia", title: "Solvencia Propiedad Inmobiliaria", icon: CreditCard },
  { id: "expediente-digital", title: "Expediente Digital", icon: Briefcase },
  { id: "mapeo", title: "Mapeo", icon: Map }
];

// Submódulos de Liquidaciones
const liquidacionesSubmenus = [
  { id: "datos-declaracion", title: "Datos de la Declaración", icon: FileText },
  { id: "documentos-soporte", title: "Cargar Documentos Soporte", icon: Briefcase }
];

// Submódulos de Desarrollo Urbano
const desarrolloUrbanoSubmenus = [
  { id: "boletin-calculo", title: "Boletín - Cálculo", icon: Calculator },
  { id: "conformidad-uso", title: "Conformidad de Uso", icon: FileText }
];

// Submódulos de Recaudaciones
const recaudacionesSubmenus = [
  { id: "comprobantes-pago", title: "Comprobantes de Pago", icon: FileText },
  { id: "comprobantes-pago-catastro", title: "Comprobantes Pago Catastro", icon: FileText },
  { id: "comprobantes-pago-desarrollo-urbano", title: "Comprobantes Pago Des. Urbano", icon: FileText }
];

// Submódulos de Publicidad
const publicidadSubmenus = [
  { id: "registro", title: "Registro", icon: FileText },
  { id: "calculo", title: "Cálculo", icon: FileText },
  { id: "solvencia-publicidad", title: "Solvencia Publicidad", icon: Shield },
  { id: "clasificador", title: "Clasificador Tasas Publicidad", icon: FileText },
  { id: "clasificador-impuestos", title: "Clasificador Impuestos Publicidad", icon: FileText }
];

// Submódulos de Vehículos
const vehiculosSubmenus = [
  { id: "registro", title: "Registro Vehicular", icon: FileText },
  { id: "calculo", title: "Cálculo de Vehículo", icon: Calculator },
  { id: "clasificador", title: "Clasificador de Vehículos", icon: FileText }
];

// Submódulos de Vendedores Ambulantes
const ambulantesSubmenus = [
  { id: "registro", title: "Registro de Vendedores", icon: FileText },
  { id: "clasificador", title: "Clasificador Ambulantes", icon: FileText },
  { id: "calculo", title: "Cálculo Vendedores Ambulantes", icon: Calculator }
];

// Submódulos de Licores
const licoresSubmenus = [
  { id: "registro", title: "Registro Licores", icon: FileText },
  { id: "calculo", title: "Cálculo Licores", icon: Calculator },
  { id: "codigos", title: "Códigos de Licores", icon: FileText },
  { id: "clasificador", title: "Clasificador de Licores", icon: FileText }
];

// Menú para el Portal del Contribuyente
const contribuyenteMenuItems = [
  { id: "portal-dashboard", title: "Mi Portal", icon: LayoutDashboard, path: "/portal/dashboard", color: "text-green-400" },
  { id: "portal-declaracion", title: "Declaración", icon: FileText, path: "/portal/declaracion", color: "text-blue-400" },
  { id: "portal-reporte-pago", title: "Reporte de Pago", icon: Wallet, path: "/portal/reporte-pago", color: "text-amber-400" },
  { id: "portal-comprobantes", title: "Comprobantes de Pago", icon: Receipt, path: "/portal/comprobantes", color: "text-emerald-400" },
  { id: "portal-pendientes", title: "Pendientes de Pago", icon: ClipboardList, path: "/portal/pendientes", color: "text-red-400" },
  { id: "perfil", title: "Mi Perfil", icon: UserCircle, path: "/perfil", color: "text-teal-400" }
];

const menuItems = [
  { id: "dashboard", title: "Dashboard", icon: LayoutDashboard, path: "/dashboard", color: "text-sky-400" },
  { id: "catastro", title: "Catastro", icon: Map, path: "/catastro", color: "text-blue-400", hasSubmenu: true, submenuType: "catastro" },
  { id: "desarrollo-urbano", title: "Desarrollo Urbano", icon: Building2, path: "/desarrollo-urbano", color: "text-green-400", hasSubmenu: true, submenuType: "desarrollo-urbano" },
  { id: "publicidad", title: "Publicidad", icon: Grid3X3, path: "/publicidad", color: "text-purple-400", hasSubmenu: true, submenuType: "publicidad" },
  { id: "vehiculos", title: "Vehículos", icon: Car, path: "/vehiculos", color: "text-amber-400", hasSubmenu: true, submenuType: "vehiculos" },
  { id: "ambulantes", title: "Vendedores Ambulantes", icon: ShoppingBag, path: "/ambulantes", color: "text-orange-400", hasSubmenu: true, submenuType: "ambulantes" },
  { id: "licores", title: "Licores", icon: Wine, path: "/licores", color: "text-amber-400", hasSubmenu: true, submenuType: "licores" },
  { id: "definiciones", title: "Definiciones", icon: BookOpen, path: "/definiciones", color: "text-orange-400", hasSubmenu: true, submenuType: "definiciones" },
  { id: "contribuyentes", title: "Contribuyentes", icon: Users, path: "/contribuyentes", color: "text-cyan-400", hasSubmenu: true, submenuType: "contribuyentes" },
  { id: "lae", title: "Permisos Municipales", icon: FileText, path: "/lae", color: "text-yellow-400" },
  { id: "cementerio", title: "Cementerio Municipal", icon: Map, path: "/cementerio", color: "text-stone-400" },
  { id: "aseo-urbano", title: "Aseo Urbano", icon: Truck, path: "/aseo-urbano", color: "text-lime-400" },
  { id: "liquidaciones", title: "Liquidaciones", icon: Calculator, path: "/liquidaciones", color: "text-pink-400", hasSubmenu: true, submenuType: "liquidaciones" },
  { id: "recaudaciones", title: "Recaudaciones", icon: Wallet, path: "/recaudaciones", color: "text-emerald-400", hasSubmenu: true, submenuType: "recaudaciones" },
  { id: "reportes", title: "Reportes", icon: BarChart3, path: "/reportes", color: "text-indigo-400" },
  { id: "configuracion", title: "Configuración", icon: Settings, path: "/configuracion", color: "text-slate-400" },
  { id: "perfil", title: "Perfil de Usuario", icon: UserCircle, path: "/perfil", color: "text-teal-400" }
];

const DashboardLayout = () => {
  const { user, logout, API, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Single state for which menu is open (only one at a time)
  const getInitialOpenMenu = () => {
    const path = location.pathname;
    if (path.startsWith("/definiciones")) return "definiciones";
    if (path.startsWith("/contribuyentes")) return "contribuyentes";
    if (path.startsWith("/catastro")) return "catastro";
    if (path.startsWith("/liquidaciones")) return "liquidaciones";
    if (path.startsWith("/desarrollo-urbano")) return "desarrolloUrbano";
    if (path.startsWith("/recaudaciones")) return "recaudaciones";
    if (path.startsWith("/publicidad")) return "publicidad";
    if (path.startsWith("/vehiculos")) return "vehiculos";
    if (path.startsWith("/ambulantes")) return "ambulantes";
    if (path.startsWith("/licores")) return "licores";
    return null;
  };
  const [openMenu, setOpenMenu] = useState(getInitialOpenMenu);
  const [securityAlert, setSecurityAlert] = useState(null);

  // Fetch security summary para mostrar alertas contextuales en el avatar
  useEffect(() => {
    if (!token) return;
    const controller = new AbortController();
    axios
      .get(`${API}/auth/security/summary`, {
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal,
      })
      .then((r) => {
        const d = r.data || {};
        const p = d.password || {};
        if (p.force_change) return setSecurityAlert("Debe cambiar su contraseña");
        if (p.expired) return setSecurityAlert("Contraseña expirada");
        if (p.grace_days_remaining > 0 && p.grace_days_remaining <= 7 && !p.compliant) {
          return setSecurityAlert(`Quedan ${p.grace_days_remaining} días para actualizar contraseña`);
        }
        if (!d.mfa_enabled && d.mfa_required_for_role) {
          return setSecurityAlert("Active MFA (recomendado)");
        }
        if (p.expires_soon) return setSecurityAlert("Contraseña vence pronto");
        setSecurityAlert(null);
      })
      .catch(() => {});
    return () => controller.abort();
  }, [API, token, location.pathname]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  const isSubmenuActive = (submenuId, parentPath = "/definiciones") => {
    return location.pathname === `${parentPath}/${submenuId}`;
  };

  const SidebarContent = () => {
    const isContribuyente = user?.role === "contribuyente";
    const activeMenuItems = isContribuyente ? contribuyenteMenuItems : menuItems;

    return (
    <>
      {/* Logo */}
      <div className="p-4 border-b border-white/10">
        <Link to={isContribuyente ? "/portal/dashboard" : "/dashboard"} className="flex items-center gap-3">
          <div className={`w-10 h-10 ${isContribuyente ? "bg-green-700" : "bg-blue-700"} rounded-lg flex items-center justify-center flex-shrink-0`}>
            <Building2 className="w-5 h-5 text-yellow-400" />
          </div>
          {sidebarOpen && (
            <div className="overflow-hidden">
              <h1 className="text-white font-bold text-sm leading-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {isContribuyente ? "SIRET Portal" : "SIRET"}
              </h1>
              <p className="text-slate-400 text-xs">{isContribuyente ? "Portal del Contribuyente" : "Sistema de Recaudacion Tributaria"}</p>
            </div>
          )}
        </Link>
      </div>

      {/* Menu Title */}
      {sidebarOpen && (
        <div className="px-4 py-3">
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
            {isContribuyente ? "Mi Portal" : "Menu Principal"}
          </span>
        </div>
      )}

      {/* Menu Items */}
      <ScrollArea className="flex-1 px-2">
        <nav className="space-y-1 py-2">
          {isContribuyente ? (
            /* Contribuyente: simple menu without submenus */
            activeMenuItems.map((item, index) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              return (
                <Link
                  key={item.id}
                  to={item.path}
                  data-testid={`menu-${item.id}`}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 group ${
                    active 
                      ? "bg-green-700/30 text-white border-l-4 border-yellow-400 -ml-0.5 pl-[10px]" 
                      : "text-slate-300 hover:bg-white/5 hover:text-white"
                  }`}
                >
                  <Icon className={`w-5 h-5 flex-shrink-0 ${active ? "text-yellow-400" : item.color + " group-hover:text-white"}`} />
                  {sidebarOpen && <span className="text-sm font-medium">{item.title}</span>}
                </Link>
              );
            })
          ) : (
          /* Funcionario: existing menu with submenus */
          menuItems.map((item, index) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            
            // Special handling for menus with submenus
            if (item.hasSubmenu) {
              const isDefiniciones = item.submenuType === "definiciones";
              const isContribuyentes = item.submenuType === "contribuyentes";
              const isCatastro = item.submenuType === "catastro";
              const isLiquidaciones = item.submenuType === "liquidaciones";
              const isDesarrolloUrbano = item.submenuType === "desarrollo-urbano";
              const isRecaudaciones = item.submenuType === "recaudaciones";
              const isPublicidad = item.submenuType === "publicidad";
              const isVehiculos = item.submenuType === "vehiculos";
              const isAmbulantes = item.submenuType === "ambulantes";
              const isLicores = item.submenuType === "licores";
              
              let submenus, isOpen, menuKey;
              if (isDefiniciones) {
                submenus = definicionesSubmenus;
                menuKey = "definiciones";
              } else if (isContribuyentes) {
                submenus = contribuyentesSubmenus;
                menuKey = "contribuyentes";
              } else if (isCatastro) {
                submenus = catastroSubmenus;
                menuKey = "catastro";
              } else if (isLiquidaciones) {
                submenus = liquidacionesSubmenus;
                menuKey = "liquidaciones";
              } else if (isDesarrolloUrbano) {
                submenus = desarrolloUrbanoSubmenus;
                menuKey = "desarrolloUrbano";
              } else if (isRecaudaciones) {
                submenus = recaudacionesSubmenus;
                menuKey = "recaudaciones";
              } else if (isPublicidad) {
                submenus = publicidadSubmenus;
                menuKey = "publicidad";
              } else if (isVehiculos) {
                submenus = vehiculosSubmenus;
                menuKey = "vehiculos";
              } else if (isAmbulantes) {
                submenus = ambulantesSubmenus;
                menuKey = "ambulantes";
              } else if (isLicores) {
                submenus = licoresSubmenus;
                menuKey = "licores";
              }
              isOpen = openMenu === menuKey;
              
              return (
                <Collapsible 
                  key={item.id} 
                  open={isOpen} 
                  onOpenChange={(open) => setOpenMenu(open ? menuKey : null)}
                >
                  <CollapsibleTrigger asChild>
                    <button
                      data-testid={`menu-${item.id}`}
                      className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg transition-all duration-200 group ${
                        active 
                          ? "bg-blue-700/30 text-white border-l-4 border-yellow-400 -ml-0.5 pl-[10px]" 
                          : "text-slate-300 hover:bg-white/5 hover:text-white"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <Icon className={`w-5 h-5 flex-shrink-0 ${active ? "text-yellow-400" : item.color + " group-hover:text-white"}`} />
                        {sidebarOpen && <span className="text-sm font-medium">{item.title}</span>}
                      </div>
                      {sidebarOpen && (
                        <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
                      )}
                    </button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="mt-1">
                    {sidebarOpen && (
                      <div className="ml-4 pl-4 border-l border-slate-700 space-y-1">
                        {submenus.map((submenu) => {
                          const SubIcon = submenu.icon;
                          const subActive = isSubmenuActive(submenu.id, item.path);
                          return (
                            <Link
                              key={submenu.id}
                              to={`${item.path}/${submenu.id}`}
                              data-testid={`submenu-${submenu.id}`}
                              className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-all duration-200 ${
                                subActive 
                                  ? "bg-blue-700/40 text-yellow-400" 
                                  : "text-slate-400 hover:bg-white/5 hover:text-white"
                              }`}
                            >
                              <SubIcon className="w-4 h-4" />
                              <span>{submenu.title}</span>
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </CollapsibleContent>
                </Collapsible>
              );
            }
            
            return (
              <Link
                key={item.id}
                to={item.path}
                data-testid={`menu-${item.id}`}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 group animate-fade-in stagger-${index + 1} ${
                  active 
                    ? "bg-blue-700/30 text-white border-l-4 border-yellow-400 -ml-0.5 pl-[10px]" 
                    : "text-slate-300 hover:bg-white/5 hover:text-white"
                }`}
              >
                <Icon className={`w-5 h-5 flex-shrink-0 ${active ? "text-yellow-400" : item.color + " group-hover:text-white"}`} />
                {sidebarOpen && <span className="text-sm font-medium">{item.title}</span>}
              </Link>
            );
          })
          )}
        </nav>
      </ScrollArea>

      {/* User Info */}
      {sidebarOpen && user && (
        <div className="p-4 border-t border-white/10">
          <div className="flex items-center gap-3">
            <div className={`w-9 h-9 ${isContribuyente ? "bg-green-700" : "bg-blue-700"} rounded-full flex items-center justify-center flex-shrink-0`}>
              <span className="text-white text-sm font-semibold">
                {user.nombre?.charAt(0)}{user.apellido?.charAt(0)}
              </span>
            </div>
            <div className="overflow-hidden">
              <p className="text-white text-sm font-medium truncate">{user.nombre} {user.apellido}</p>
              <p className="text-slate-400 text-xs capitalize">{user.role}</p>
            </div>
          </div>
        </div>
      )}
    </>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="dashboard-layout">
      {/* Desktop Sidebar */}
      <aside 
        className={`hidden lg:flex flex-col sidebar-transition ${sidebarOpen ? "w-64" : "w-20"} bg-slate-900 fixed h-screen z-30`}
        data-testid="sidebar"
      >
        <SidebarContent />
      </aside>

      {/* Mobile Sidebar Overlay */}
      {mobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <aside 
        className={`lg:hidden fixed inset-y-0 left-0 w-64 bg-slate-900 z-50 transform transition-transform duration-300 ${
          mobileMenuOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="absolute top-4 right-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileMenuOpen(false)}
            className="text-white hover:bg-white/10"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>
        <div className="flex flex-col h-full">
          <SidebarContent />
        </div>
      </aside>

      {/* Main Content */}
      <div className={`flex-1 flex flex-col ${sidebarOpen ? "lg:ml-64" : "lg:ml-20"} transition-all duration-300`}>
        {/* Header */}
        <header className="bg-white border-b border-slate-200 sticky top-0 z-20" data-testid="header">
          <div className="flex items-center justify-between px-4 lg:px-6 py-3">
            {/* Left Side */}
            <div className="flex items-center gap-4">
              {/* Mobile Menu Button */}
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden"
                onClick={() => setMobileMenuOpen(true)}
                data-testid="mobile-menu-btn"
              >
                <Menu className="w-5 h-5" />
              </Button>
              
              {/* Collapse Button (Desktop) */}
              <Button
                variant="ghost"
                size="icon"
                className="hidden lg:flex"
                onClick={() => setSidebarOpen(!sidebarOpen)}
                data-testid="sidebar-toggle"
              >
                <Menu className="w-5 h-5" />
              </Button>

              {/* Search */}
              <div className="hidden md:flex items-center gap-2 bg-slate-100 rounded-lg px-3 py-2 w-64">
                <Search className="w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Buscar..."
                  className="bg-transparent border-none outline-none text-sm text-slate-700 w-full"
                  data-testid="search-input"
                />
              </div>
            </div>

            {/* Right Side */}
            <div className="flex items-center gap-3">
              {/* Notifications */}
              <Button variant="ghost" size="icon" className="relative" data-testid="notifications-btn">
                <Bell className="w-5 h-5 text-slate-600" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
              </Button>

              {/* User Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2 relative" data-testid="user-menu-btn">
                    <div className="w-8 h-8 bg-blue-800 rounded-full flex items-center justify-center">
                      <span className="text-white text-xs font-semibold">
                        {user?.nombre?.charAt(0)}{user?.apellido?.charAt(0)}
                      </span>
                    </div>
                    {securityAlert && (
                      <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-red-500 rounded-full ring-2 ring-white" data-testid="security-alert-dot" />
                    )}
                    <div className="hidden md:block text-left">
                      <p className="text-sm font-medium text-slate-700">{user?.nombre}</p>
                      <p className="text-xs text-slate-500 capitalize">{user?.role}</p>
                    </div>
                    <ChevronDown className="w-4 h-4 text-slate-400" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>Mi Cuenta</DropdownMenuLabel>
                  {securityAlert && (
                    <DropdownMenuItem onClick={() => navigate("/perfil/seguridad")} className="text-amber-700 bg-amber-50" data-testid="menu-security-alert">
                      <ShieldAlert className="w-4 h-4 mr-2" />
                      {securityAlert}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/perfil")} data-testid="menu-profile">
                    <UserCircle className="w-4 h-4 mr-2" />
                    Perfil
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/perfil/seguridad")} data-testid="menu-seguridad">
                    <Shield className="w-4 h-4 mr-2" />
                    Seguridad
                  </DropdownMenuItem>
                  {user?.role !== "contribuyente" && (
                  <DropdownMenuItem onClick={() => navigate("/configuracion")} data-testid="menu-settings">
                    <Settings className="w-4 h-4 mr-2" />
                    Configuración
                  </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="menu-logout">
                    <LogOut className="w-4 h-4 mr-2" />
                    Cerrar Sesión
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-4 lg:p-6" data-testid="main-content">
          <Outlet />
        </main>

        {/* Footer */}
        <footer className="bg-white border-t border-slate-200 px-6 py-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-2 text-sm text-slate-500">
            <p>© 2024 Sistema de Recaudación Tributaria Municipal</p>
            <p>Venezuela - Todos los derechos reservados</p>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default DashboardLayout;
